export const BadgeTier = {
    Wood: 'wood',
    Bronze: 'bronze',
    Silver: 'silver',
    Gold: 'gold',
} as const;
export type BadgeTier = typeof BadgeTier[keyof typeof BadgeTier];

export const BadgeCategory = {
    Player: 'player',
    GM: 'gm',
    SystemsPlayed: 'systems_played',
    SystemsGmed: 'systems_gmed',
    RepeatedGamesGmed: 'repeated_games_gmed',
    Streak: 'streak',
    Mentors: 'mentors',
    UniquePlayersGmed: 'unique_players_gmed',
    Special: 'special',
} as const;
export type BadgeCategory = typeof BadgeCategory[keyof typeof BadgeCategory];

export const BadgeSource = {
    Auto: 'auto',
    Manual: 'manual',
} as const;
export type BadgeSource = typeof BadgeSource[keyof typeof BadgeSource];

export type DieSides = 4 | 6 | 8 | 10 | 12 | 20;

/**
 * Progression visuals default to a single die. '2d6' renders an overlaid pair.
 * 'flame' renders an orange flame path (the streak track) — for this variant
 * `dieSides` is omitted.
 */
export type ProgressionBadgeVariant = 'die' | '2d6' | 'flame';

export type ProgressionBadgeDefinition = {
    key: string;
    category: Exclude<BadgeCategory, 'special'>;
    tier: BadgeTier;
    /** Required for die-shaped variants; omitted for `flame`. */
    dieSides?: DieSides;
    title: string;
    /**
     * Feminine title override. Only set where the masculine form has a
     * distinct, idiomatic feminine equivalent — otherwise the masculine
     * title is used for both genders.
     */
    titleFemale?: string;
    /**
     * Description template. May contain inline gender markers like
     * `(masc|fem)` — resolved at render via `applyGender`.
     */
    description: string;
    threshold: number;
    variant?: ProgressionBadgeVariant;
};

export type SpecialBadgeDefinition = {
    key: string;
    category: typeof BadgeCategory.Special;
    title: string;
    /** See ProgressionBadgeDefinition.titleFemale. */
    titleFemale?: string;
    /** See ProgressionBadgeDefinition.description. */
    description: string;
    /** A material design icon name fallback when no SVG variant is available. */
    icon?: string;
    /** Visual key to pick a special SVG, e.g. 'crown' for Megadungeon Winner. */
    visual: 'crown' | 'star' | 'trophy';
};

export type BadgeDefinition = ProgressionBadgeDefinition | SpecialBadgeDefinition;

export type UserBadge = {
    /** Database row id. Absent for synthetic/mock badges that aren't backed by a row. */
    id?: string;
    key: string;
    source: BadgeSource;
    awardedAt: Date;
    awardedByUsername?: string;
    notes?: string;
    /** Current activity count that drives the badge (e.g. games GM'd). Not set for special awards. */
    count?: number;
};

export type ResolvedUserBadge = UserBadge & {
    definition: BadgeDefinition;
};

export type BadgeAggregateCounts = {
    gmCount: number;
    playerCount: number;
    systemsPlayedCount: number;
    systemsGmedCount: number;
    /**
     * Largest group of games this user GM'd that share either a normalised
     * title or a normalised description, scoped to the same system.
     */
    maxRepeatedGmedCount: number;
    /** Current run of consecutive concluded events attended as GM or player. */
    currentStreak: number;
    /** Longest historical run of consecutive concluded events attended. */
    longestStreak: number;
    /** Distinct (non-bot) GMs the user has played a counted game under. */
    mentorsCount: number;
    /** Distinct (non-bot) players who have joined a counted game this user GM'd. */
    uniquePlayersGmedCount: number;
};
